/**
 * CDN URL Helper
 * Generates CDN URLs for league, team, and venue images
 */

/**
 * Map sport_id to sport slug
 */
const SPORT_SLUG_MAP = {
    1: 'football',
    2: 'baseball',
    3: 'basketball',
    4: 'nba',
};

/**
 * Detect environment from window location
 * @returns {string} Environment ('local', 'dev', 'com')
 */
const detectEnvironment = () => {
    if (typeof window === 'undefined') {
        return 'local';
    }
    
    const hostname = window.location.hostname;
    if (hostname.includes('.dev')) {
        return 'dev';
    }
    if (hostname.includes('.com') || hostname.includes('caribe7.com')) {
        return 'com';
    }
    return 'local';
};

/**
 * Map environment to domain suffix
 */
const getDomain = (env) => {
    const domainMap = {
        'local': 'caribe7.local',
        'dev': 'caribe7.com', // Dev uses caribe7.com (same as prod)
        'com': 'caribe7.com',
    };
    return domainMap[env] || domainMap['local'];
};

/**
 * Get the CDN protocol based on environment
 * Returns 'http' for local, 'https' for dev/com
 * 
 * @param {string} env - Optional environment ('local', 'dev', 'com'). If not provided, will detect from window.location
 * @returns {string} Protocol ('http' or 'https')
 */
const getCdnProtocol = (env) => {
    const environment = env || detectEnvironment();
    return environment === 'local' ? 'http' : 'https';
};

/**
 * Get CDN URL for an image
 * 
 * @param {string} type - Image type: 'league', 'team', 'venue', 'athlete', or 'sports'
 * @param {number} id - ID of the league, team, venue, athlete, or sport
 * @param {number} sportId - DEPRECATED: No longer required. Kept for backward compatibility but ignored.
 * @param {string} env - Optional environment ('local', 'dev', 'com'). If not provided, will detect from window.location
 * @returns {string} CDN URL for the image
 */
export const getCdnImageUrl = (type, id, sportId = null, env = null) => {
    if (!id) {
        return '';
    }
    
    const environment = env || detectEnvironment();
    const protocol = getCdnProtocol(environment);
    const domain = getDomain(environment);
    
    // Handle 'sports' type separately (no sport slug needed)
    if (type === 'sports') {
        return `${protocol}://cdn.${domain}/assets/images/sportsbook/sports/${id}.webp`;
    }
    
    // Map type to directory name (league -> leagues, team -> teams, venue -> venues, athlete -> athletes)
    const typeMap = {
        'league': 'leagues',
        'team': 'teams',
        'venue': 'venues',
        'athlete': 'athletes',
    };
    const directoryName = typeMap[type] || type;
    
    return `${protocol}://cdn.${domain}/assets/images/sportsbook/${directoryName}/${id}.webp`;
};

/**
 * Get sport slug from sport ID
 * 
 * @param {number} sportId - Sport ID
 * @returns {string} Sport slug
 */
export const getSportSlug = (sportId) => {
    return SPORT_SLUG_MAP[sportId] || 'football';
};

